/*
 * Copyright © 2020-2023 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: yongqiang
 * @LastEditTime: 2023-10-19 16:00:40
 */
import React, { useState } from 'react'
import { Button, Divider, Layout, Modal, Spin } from 'antd'
import { DownloadOutlined } from '@ant-design/icons'
import excel_icon from '../../../images/excel_icon.jpeg'
import FileSaver from 'file-saver'
import CONFIG from '../../../constants/config'
import { useAppDispatch } from '../../../app/hooks'

import {
  fetchLocationsByAddress,
  importGeoData
} from '../../../actions/geoData'
import Upload from './Upload'
import { exportExcel, generateExcelParams } from './exportExcel'
import Preview from './Preview'
import Amap from './amap'
import type { GeoItem, Position, LocateInfo } from './types'

const { Content } = Layout

// 上传的位置信息
interface LocationInput {
  content: string
  address: string
  lat: string
  lng: string
}

interface BatchImportProps {
  onFinish: () => void
  token: string
  reportSaClickEvent: (event: string) => void
  reportSaUploadEvent: (event: string) => void
  afterUpload: (fileName: string) => void
}

const BatchImport: React.FC<BatchImportProps> = (props) => {
  const { onFinish, token, reportSaClickEvent, reportSaUploadEvent, afterUpload } =
    props

  const [geoItems, setGeoItems] = useState<GeoItem[]>([])
  const [isVisibleResults, setIsVisibleResults] = useState(false)
  const [isVisibleMap, setIsVisibleMap] = useState(false)
  const [editRecord, setEditRecord] = useState<GeoItem | null>(null)
  const [loading, setLoading] = useState(false)
  const dispatch = useAppDispatch()

  const getLocationsByAddress = (locations: LocationInput[]) => {
    const locationList = locations.map((location) => ({
      token,
      ...location
    }))
    setLoading(true)
    dispatch(
      fetchLocationsByAddress({
        records: locationList
      })
    ).then((data: any) => {
      if (data.status === 0) {
        const items: GeoItem[] = data.records.map(
          (item: Omit<GeoItem, 'id'>, index: number) => ({
            ...item,
            id: index + 1
          })
        )
        setGeoItems(items)
        setIsVisibleResults(true)
        setLoading(false)
      }
    })
  }

  const handleImport = () => {
    const record = geoItems.map((item) => ({
      token: token,
      ...item
    }))

    return dispatch(importGeoData({ record }))
  }

  // 确认导入按钮
  const handleImportClick = () => {
    reportSaClickEvent('confirm')
    handleImport().then((data: any) => {
      if (data.status === 0) {
        reportSaUploadEvent('success')
        Modal.success({
          title: '导入成功，请在手机上查看',
          onOk: onFinish
        })
      } else if (data.status === 1) {
        reportSaUploadEvent('expiredToken')
        Modal.error({
          title: '手机端已停止上传请前往手机端重新操作',
          onOk: onFinish
        })
      } else {
        Modal.error({
          title: data.msg
        })
      }
    })
  }

  // 下载定位失败列表
  const handleLocationsErrorClick = () => {
    reportSaClickEvent('downloadErrorItem')

    exportExcel(
      generateExcelParams,
      geoItems.filter((item) => item.status !== 1)
    )
  }

  // 调整坐标
  const adjustPosition = (location: Position, address: string) => {
    reportSaClickEvent('adjustPosition')
    const { longitude, latitude } = location
    const newRecord: GeoItem = {
      ...editRecord!,
      lng: String(longitude),
      lat: String(latitude),
      address: address,
      status: 1
    }

    const newGeoItems = geoItems.map((item) => {
      if (item.id === newRecord.id) {
        return newRecord
      }
      return item
    })

    setGeoItems(newGeoItems)
    setEditRecord(null)
    setIsVisibleMap(false)
  }

  return (
    <Content className="content geo-data-content">
      <div className="batch-import-container">
        <div className="operate-wrapper">
          <div className="step1">
            <div className="title">1.下载模板，填写水印选项</div>
            <img src={excel_icon} className="excel-icon" alt="" />
            <div>《水印选项模板》</div>
            <Button
              icon={<DownloadOutlined />}
              className="download-template"
              onClick={() => {
                FileSaver.saveAs(CONFIG.geoDataExcelUrl, '水印选项模板.xlsx')
                reportSaClickEvent('download')
              }}
            >
              立即下载
            </Button>
          </div>
          <div className="step2">
            <div className="title">2.上传填写完毕的Excel文件</div>
            <Upload
              getLocationsByAddress={getLocationsByAddress}
              reportSaClickEvent={reportSaClickEvent}
              reportSaUploadEvent={reportSaUploadEvent}
              afterUpload={afterUpload}
            />
          </div>
        </div>

        {isVisibleResults && (
          <>
            <Divider className="divider" />
            <Preview
              onAdjust={(record: GeoItem) => {
                setIsVisibleMap(true)
                setEditRecord(record)
              }}
              geoItems={geoItems}
              onImportClick={handleImportClick}
              onLocationsErrorClick={handleLocationsErrorClick}
            />
          </>
        )}
      </div>

      <Modal
        open={loading}
        footer={null}
        closable={false}
        getContainer={false}
        width={304}
      >
        <div className="loading-wrapper">
          <Spin size="large" />
          <div className="loading-text">上传成功，正在解析...</div>
        </div>
      </Modal>

      {isVisibleMap && editRecord && (
        <Amap
          handleOk={(location: Position, address: string) => {
            adjustPosition(location, address)
          }}
          handleCancel={() => {
            setIsVisibleMap(false)
          }}
          locateInfo={editRecord as LocateInfo}
        />
      )}
    </Content>
  )
}

export default BatchImport
